/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors } from '../../shared/theme/ticketColors';
import { currencyFormatter, formatStockQuantity } from '../application/inventoryUtils';
import { useInventoryHistoryUiState } from '../application/inventoryHistoryUiState';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import type { StockMovementRow, StockRow } from '../../inventory/stock/public-api';
import {
  extractSaleIdFromStockMovement,
  extractWorkOrderIdFromStockMovement,
} from '../../inventory/stock/public-api';
import { OperationalFilterClearLink } from '../../shared/filters/OperationalFilterClearLink'
import { OperationalChipSelectRow } from '../../shared/filters/OperationalChipSelectRow';
import { ModalStatePanel } from '../../shared/modals/ModalStatePanel'
import { ModalShell } from '../../shared/modals/ModalShell'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { formatLocalDateTime } from '@shared/vueltoDateTime';
import { InventoryIsoDateField } from '../../shared/ui/InventoryIsoDateField';

export type HistoryReasonOption = { value: string; label: string };

type Props = {
  visible: boolean;
  selectedStock: StockRow | null;
  movementHistory: StockMovementRow[];
  historyLoading: boolean;
  historyLoadingMore: boolean;
  historyHasMore: boolean;
  onLoadMore: () => void;
  onClose: () => void;
  actorLabel: (userId: string | null | undefined) => string;
  reasonOptions: HistoryReasonOption[];
  reasonFilter: string;
  onReasonFilterChange: (value: string) => void;
  fromDate: string;
  toDate: string;
  onFromDateChange: (value: string) => void;
  onToDateChange: (value: string) => void;
  onExportCsv: () => void;
  exportingCsv?: boolean;
  onViewSale: (movement: StockMovementRow) => void;
  reasonLabelFn: (reason: string | null | undefined) => string;
  layoutWide?: boolean;
};

export const MovementHistoryModal = ({
  visible,
  selectedStock,
  movementHistory,
  historyLoading,
  historyLoadingMore,
  historyHasMore,
  onLoadMore,
  onClose,
  actorLabel,
  reasonOptions,
  reasonFilter,
  onReasonFilterChange,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  onExportCsv,
  exportingCsv = false,
  onViewSale,
  reasonLabelFn,
  layoutWide = false,
}: Props) => {
  // Estado extra del flow (error remoto + owner-only #868) sin props nuevas en el call site.
  const historyError = useInventoryHistoryUiState((s) => s.historyError);
  const historyOwnerOnly = useInventoryHistoryUiState((s) => s.historyOwnerOnly);
  const retryHistoryLoad = useInventoryHistoryUiState((s) => s.retryHistoryLoad);

  const saleIdFromMovement = (m: StockMovementRow) =>
    extractSaleIdFromStockMovement({ reason: m.reason, referenceId: m.referenceId });
  // get_work_order hace assert_org_capability('work_orders') — si la org no
  // tiene la capability (o la perdió tras un cambio de rubro), el detalle es un
  // callejón sin salida con 403. Sin capability no mostramos el link.
  const workOrdersEnabled = useHasBusinessCapability('work_orders');
  const workOrderIdFromMovement = (m: StockMovementRow) =>
    workOrdersEnabled
      ? extractWorkOrderIdFromStockMovement({ reason: m.reason, referenceId: m.referenceId })
      : null;

  const hasActiveFilters =
    reasonFilter !== 'all' || fromDate.trim().length > 0 || toDate.trim().length > 0;

  const productName = selectedStock?.productName ?? 'Producto';
  // #706: si el stock es de una variante, mostrar el label de la variante en el subtítulo.
  const variantLabel = selectedStock?.variantLabel ?? null;
  const historySubtitle = variantLabel ? `${productName} · ${variantLabel}` : productName;

  const clearFilters = () => {
    onReasonFilterChange('all');
    onFromDateChange('');
    onToDateChange('');
  };

  const handleScroll = useCallback(
    ({ nativeEvent }: { nativeEvent: { layoutMeasurement: { height: number }; contentOffset: { y: number }; contentSize: { height: number } } }) => {
      const { layoutMeasurement, contentOffset, contentSize } = nativeEvent;
      const distanceFromEnd = contentSize.height - contentOffset.y - layoutMeasurement.height;
      if (distanceFromEnd < 60 && historyHasMore && !historyLoadingMore) {
        onLoadMore();
      }
    },
    [historyHasMore, historyLoadingMore, onLoadMore]
  );

  const listHeader = (
    <View style={styles.listHeader}>
      <View style={styles.filterSection}>
        <Text style={modalExperience.filtersPanelTitle}>Filtrar por motivo</Text>
        <OperationalChipSelectRow
          ticketTone
          value={reasonFilter}
          onChange={onReasonFilterChange}
          options={reasonOptions}
        />
      </View>

      <View style={styles.filterSection}>
        <Text style={modalExperience.filtersPanelTitle}>Rango de fechas</Text>
        <View style={modalExperience.filterDateRow}>
          <View style={modalExperience.filterDateCol}>
            <Text style={modalExperience.filterDateHint}>Desde</Text>
            <InventoryIsoDateField
              value={fromDate}
              onChange={onFromDateChange}
              emptyLabel="Sin fecha"
              ariaLabel="Fecha desde"
              clearButtonLabel="Quitar"
              iosEmptyBehavior="openPicker"
            />
          </View>
          <View style={modalExperience.filterDateCol}>
            <Text style={modalExperience.filterDateHint}>Hasta</Text>
            <InventoryIsoDateField
              value={toDate}
              onChange={onToDateChange}
              emptyLabel="Sin fecha"
              ariaLabel="Fecha hasta"
              clearButtonLabel="Quitar"
              iosEmptyBehavior="openPicker"
            />
          </View>
        </View>
      </View>

      {hasActiveFilters ? (
        <View style={styles.filterClearRow}>
          <OperationalFilterClearLink onPress={clearFilters} />
        </View>
      ) : null}
    </View>
  );

  return (
    <ModalShell
      visible={visible}
      onClose={onClose}
      appearance="ticket"
      kicker="Stock"
      title="Historial"
      subtitle={historySubtitle}
      widthVariant={layoutWide ? 'comfortable' : 'standard'}
      keyboardAvoiding={false}
      scrollProps={{ onScroll: handleScroll, scrollEventThrottle: 16 }}
      scrollContentStyle={styles.scrollContent}
    >
      {/* Con owner-only no hay datos que filtrar: ocultar filtros. */}
      {!historyOwnerOnly ? listHeader : null}

      {historyLoading ? (
        <ModalStatePanel loading loadingMessage="Cargando movimientos…" />
      ) : historyOwnerOnly ? (
        <ModalStatePanel
          empty
          emptyTitle="Historial del equipo"
          emptyMessage="El historial completo lo ve el dueño de la organización."
        />
      ) : historyError ? (
        <View style={modalExperience.stateBox}>
          <Ionicons name="cloud-offline-outline" size={40} color={ticketColors.ink3} />
          <Text style={modalExperience.stateTitle}>No pudimos cargar el historial</Text>
          <Text style={modalExperience.stateText}>{historyError}</Text>
          {retryHistoryLoad ? (
            <TouchableOpacity
              style={styles.retryBtn}
              onPress={retryHistoryLoad}
              accessibilityRole="button"
              accessibilityLabel="Reintentar"
            >
              <Text style={modalExperience.accentLink}>Reintentar</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : movementHistory.length === 0 ? (
        <ModalStatePanel
          empty
          emptyTitle="Sin movimientos"
          emptyMessage="Probá ampliar el rango de fechas o elegir otro motivo."
        />
      ) : (
        <View style={styles.timelinePanel}>
          <View style={styles.timelinePanelHeader}>
            <Text style={styles.timelinePanelTitle}>Movimientos</Text>
            <Text style={styles.timelinePanelSubtitle}>{movementHistory.length} {movementHistory.length === 1 ? 'resultado' : 'resultados'}</Text>
          </View>
          <View style={styles.timeline}>
          {movementHistory.map((movement) => {
            const saleId = saleIdFromMovement(movement);
            const workOrderId = workOrderIdFromMovement(movement);
            const isPositive = movement.delta > 0;
            const actor = actorLabel(movement.performedByUserId);
            const reasonStr = movement.reason ?? '';
            const isAdjust =
              reasonStr === 'manual_adjustment' ||
              reasonStr === 'physical_count' ||
              reasonStr === 'bulk_coefficient' ||
              reasonStr === 'transfer_rejected';
            const dotKind: 'in' | 'out' | 'adjust' = isAdjust
              ? 'adjust'
              : isPositive
                ? 'in'
                : 'out';
            return (
              <View key={movement.id} style={styles.timelineItem}>
                <View
                  style={[
                    styles.timelineDot,
                    dotKind === 'in' && styles.timelineDotIn,
                    dotKind === 'out' && styles.timelineDotOut,
                    dotKind === 'adjust' && styles.timelineDotAdjust,
                  ]}
                >
                  <Ionicons
                    name={
                      dotKind === 'in'
                        ? 'arrow-down'
                        : dotKind === 'out'
                          ? 'arrow-up'
                          : 'swap-horizontal'
                    }
                    size={14}
                    color={
                      dotKind === 'in'
                        ? ticketColors.ok
                        : dotKind === 'out'
                          ? ticketColors.bad
                          : ticketColors.gold
                    }
                  />
                </View>
                <View style={styles.timelineBody}>
                  <View style={styles.timelineRow}>
                    <View style={styles.timelineText}>
                      <Text style={styles.timelineTitle} numberOfLines={2}>
                        {reasonLabelFn(movement.reason) || 'Sin motivo'}
                      </Text>
                      <Text style={styles.timelineMeta}>
                        {formatLocalDateTime(movement.createdAt)}
                        {actor ? ` · ${actor}` : ''}
                      </Text>
                    </View>
                    <Text
                      style={[
                        styles.timelineAmount,
                        dotKind === 'in' && styles.timelineAmountIn,
                        dotKind === 'out' && styles.timelineAmountOut,
                        dotKind === 'adjust' && styles.timelineAmountAdjust,
                      ]}
                    >
                      {movement.delta > 0 ? '+' : ''}
                      {formatStockQuantity(movement.delta, selectedStock?.unitOfMeasure)}
                    </Text>
                  </View>
                  {movement.unitCost != null ? (
                    <Text style={styles.timelineCost}>
                      Costo unitario {currencyFormatter.format(movement.unitCost)}
                    </Text>
                  ) : null}
                  {saleId ? (
                    <TouchableOpacity
                      style={styles.viewSaleBtn}
                      onPress={() => onViewSale(movement)}
                      accessibilityRole="button"
                      accessibilityLabel={
                        movement.reason?.startsWith('sale_void:')
                          ? 'Ver venta anulada'
                          : 'Ver venta'
                      }
                    >
                      <Text style={modalExperience.accentLink}>
                        {movement.reason?.startsWith('sale_void:')
                          ? 'Ver venta anulada'
                          : 'Ver venta'}
                      </Text>
                      <Ionicons name="chevron-forward" size={14} color={ticketColors.gold} />
                    </TouchableOpacity>
                  ) : workOrderId ? (
                    <TouchableOpacity
                      style={styles.viewSaleBtn}
                      onPress={() => onViewSale(movement)}
                      accessibilityRole="button"
                      accessibilityLabel="Ver orden de trabajo"
                    >
                      <Text style={modalExperience.accentLink}>Ver orden de trabajo</Text>
                      <Ionicons name="chevron-forward" size={14} color={ticketColors.gold} />
                    </TouchableOpacity>
                  ) : null}
                </View>
              </View>
            );
          })}

          </View>

          <View style={styles.timelineFooter}>
            {historyLoadingMore ? (
              <ActivityIndicator color={ticketColors.ink3} size="small" />
            ) : !historyHasMore && movementHistory.length > 0 ? (
              <Text style={styles.timelineFooterText}>No hay más movimientos</Text>
            ) : null}
            <TouchableOpacity
              style={[styles.exportLink, exportingCsv && styles.exportLinkDisabled]}
              onPress={onExportCsv}
              disabled={exportingCsv}
              accessibilityRole="button"
              accessibilityLabel={exportingCsv ? 'Exportando historial' : 'Exportar historial a CSV'}
            >
              <Text style={styles.exportLinkText}>{exportingCsv ? 'Exportando…' : 'Exportar CSV →'}</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </ModalShell>
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    gap: spacing.sm,
  },
  timelinePanel: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    padding: spacing.md,
    gap: 6,
  },
  timelinePanelHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    marginBottom: 4,
  },
  timelinePanelTitle: {
    fontSize: 14,
    fontWeight: '800',
    color: ticketColors.ink,
  },
  timelinePanelSubtitle: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink2,
  },
  timelineFooter: {
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
  },
  timelineFooterText: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink3,
  },
  listHeader: {
    gap: spacing.sm,
  },
  filterSection: {
    gap: spacing.xs,
  },
  filterClearRow: {
    alignItems: 'flex-end',
  },
  timeline: {
    gap: 0,
  },
  // Timeline
  timelineItem: {
    flexDirection: 'row',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  timelineDot: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  timelineDotIn: {
    borderColor: 'rgba(34, 197, 94, 0.4)',
    backgroundColor: 'rgba(34, 197, 94, 0.08)',
  },
  timelineDotOut: {
    borderColor: 'rgba(239, 68, 68, 0.4)',
    backgroundColor: 'rgba(239, 68, 68, 0.08)',
  },
  timelineDotAdjust: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
  },
  timelineBody: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  timelineRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  timelineText: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  timelineTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  timelineMeta: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink3,
  },
  timelineAmount: {
    fontSize: 14,
    fontWeight: '800',
    fontVariant: ['tabular-nums'],
    flexShrink: 0,
  },
  timelineAmountIn: { color: ticketColors.ok },
  timelineAmountOut: { color: ticketColors.bad },
  timelineAmountAdjust: { color: ticketColors.gold },
  timelineCost: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink3,
  },
  viewSaleBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    alignSelf: 'flex-start',
    marginTop: 2,
  },
  retryBtn: {
    alignSelf: 'center',
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.md,
  },
  exportLink: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.sm,
  },
  exportLinkDisabled: {
    opacity: 0.45,
  },
  exportLinkText: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.gold,
  },
});
